import { DRAFT_ONE_RCT, PC_13663, RFI, SDPD_AXON } from "./data";
import { C, fmtInt } from "./theme";
import { Callout, Card, Eyebrow, Mark, Pullquote, Section, Step, SubSection, Table, Td, Source } from "./ui";

// ===================================================================
// 1. The choice in front of the department
// ===================================================================

export function ChoiceSection() {
  return (
    <Section
      id="choice"
      number="01"
      title="The choice is not AI or no AI"
      subtitle={
        <>
          It is <strong className="text-slate-900">sanctioned AI or unsanctioned AI</strong>.
          Officers who are not given an approved tool do not write reports the slow way.
          They open a consumer chatbot on a personal phone, ChatGPT or Gemini or Claude or
          Grok, and paste the facts of a case into it.
        </>
      }
    >
      <p className="text-slate-700 leading-relaxed max-w-3xl">
        This is the part of the problem that does not appear in a budget line. It is
        already happening in departments across the country, it leaves no trace in any
        system the department controls, and as of January 1, 2026 it puts the agency,
        not the officer, in violation of California law.
      </p>

      <SubSection
        title="What an unsanctioned tool actually costs"
        subtitle={
          <>
            When criminal justice information is pasted into a consumer service, the
            department loses every control it would otherwise have. There is no record
            that AI was used, no retained draft, no audit trail, and no contract
            governing what happens to the data.
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-4">
          <Card title="No disclosure, and no way to make one">
            Nobody knows a report was AI-assisted, so the disclosure California now
            requires never appears. The omission surfaces later, on the stand.
          </Card>
          <Card title="No retained first draft">
            The original AI output is gone the moment the officer closes the tab. It
            cannot be produced in discovery because it was never captured.
          </Card>
          <Card title="No audit trail">
            The department cannot say who used AI, on which report, or from what source
            material, because none of it happened inside a department system.
          </Card>
          <Card title="No contractual restriction on the data">
            A consumer service is not a contracted vendor. The statutory limits on
            selling, sharing, or otherwise using agency information never attach.
          </Card>
        </div>

        <div className="mt-5">
          <Callout tone="warning" title="Where that information actually ends up">
            <p>
              On their consumer tiers, these services generally use what people type into
              them to train their own models. The opt-out exists, but it sits in an account
              setting most users never open, and conversations can be retained for years
              and reviewed by humans in the meantime.
            </p>
            <p>
              So the victim's name, the suspect's statement, the address, the date of
              birth, the injuries, and the officer's own account of what happened leave the
              department's control completely and become training material for a commercial
              model the department has no relationship with. There is no data processing
              agreement, no CJIS addendum, no audit right, and no deletion the department
              can compel.
            </p>
            <p>
              And the department cannot know any of it happened, because none of it touched
              a system it controls.
            </p>
          </Callout>
        </div>

        <div className="mt-4">
          <Callout tone="legal" title="Where our models run">
            <p>
              This is not an argument that those models are unsafe. It is an argument about
              where they run and who controls the boundary around them.
            </p>
            <p>
              Our models run on <strong>Amazon Bedrock inside AWS GovCloud</strong>, in a
              protected environment the department's data never leaves. Criminal justice
              information is processed in the same government-controlled boundary where it
              is stored. It is not handed to a third-party consumer service, and{" "}
              <strong>no agency data is used to train any model</strong>. That is a
              contractual commitment, not a setting somebody has to remember to switch off.
            </p>
            <p>
              An officer pasting a case into a consumer app and a department procuring under
              contract are not doing the same thing, and only one of them is defensible.
            </p>
          </Callout>
        </div>
      </SubSection>

      <SubSection
        title={`${PC_13663.billName}, now ${PC_13663.codeSection}`}
        subtitle={
          <>
            Signed {PC_13663.signedDate}, effective {PC_13663.effectiveDate}. It applies
            both to systems that draft narratives from body-worn camera footage and to
            systems that work from an officer's dictated report, so it applies to us,
            and we say so plainly. Below is every operative element, and who satisfies it.
          </>
        }
      >
        <Table
          minWidth={720}
          head={[
            "Statutory element",
            "Requirement",
            "Officer using a consumer chatbot",
            "Police Narratives AI",
          ]}
        >
          {PC_13663.elements
            .filter((e) => e.cite !== "§ 13663(d)(2)" && e.cite !== "§ 13663(e)(1)")
            .map((e) => (
              <tr key={e.cite}>
                <Td strong className="whitespace-nowrap font-mono text-xs">
                  {e.cite}
                </Td>
                <Td>
                  <span className="font-medium text-slate-800">{e.requirement}</span>
                  <div className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {e.detail}
                  </div>
                </Td>
                <Td>
                  <Mark value="no" />
                </Td>
                <Td>
                  <Mark value="yes" />
                </Td>
              </tr>
            ))}
        </Table>

        <div className="mt-5">
          <Callout tone="legal" title="Who carries the violation">
            <p>
              Every duty in {PC_13663.codeSection} lands on the{" "}
              <strong>law enforcement agency</strong>, not on the individual officer who
              opened the chatbot. A department with no sanctioned tool is not neutral on
              this question. It is accumulating unrecorded, undisclosed, unretained
              AI-assisted reports that it has no way to identify after the fact.
            </p>
          </Callout>
        </div>
      </SubSection>

      <SubSection
        title="We helped write this law"
        subtitle="A standard we helped shape, then built to before it took effect."
      >
        <Callout tone="good">
          <p>
            Police Narratives AI advised the office of{" "}
            <strong>Senator Jesse Arreguín</strong> during the drafting of {PC_13663.billName}.
            We argued for the provisions that matter most to a department's legal
            position: retention of the first draft, an audit trail that ties a report to
            its source recordings, and the restriction on what a vendor may do with
            agency data.
          </p>
          <p>
            We built the product to those requirements before they were requirements.
            When SDPD evaluates vendors against {PC_13663.codeSection}, it is evaluating
            them against a standard we helped write.
          </p>
        </Callout>
      </SubSection>

      <SubSection
        title="The exposure that shows up in court"
        subtitle="Report-writing practice becomes an impeachment question long before it becomes an audit finding."
      >
        <p className="text-sm text-slate-700 leading-relaxed max-w-3xl">
          An officer who cannot explain how their report was produced is an officer a
          defense attorney can work with. The questions are obvious and they are coming:{" "}
          <em>Did you write this report? What wrote the first version? Do you still have
          it? What did you change? Can you produce the recording it came from?</em> A
          department that can answer all five in writing is in a materially different
          position from one that cannot answer any.
        </p>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <Card title="Retained">
            The first draft, the source audio, the transcript, and the exported final
            report are all retained and linked to the case.
          </Card>
          <Card title="Attributable">
            The audit trail records who used AI, on which report, and against which
            recordings.
          </Card>
          <Card title="Attested">
            The officer affirms, on export, that they reviewed the report and that the
            facts are true and correct.
          </Card>
        </div>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// 2. The officer is the author
// ===================================================================

const CAMERA_CAPTURES = [
  "What was said, within microphone range",
  "Timestamps and duration",
  "Ambient sound the microphone could register",
];

const ONLY_THE_OFFICER = [
  "What they knew before arrival: priors, dispatch context, prior contacts at the address",
  "What they perceived that the sensor did not: odor of an accelerant, a bulge in a waistband, a furtive movement behind the lens",
  "Why they made the decision they made, in the second they made it",
  "What they observed before the camera was activated and after it was deactivated",
  "Training and experience that made an ambiguous cue meaningful",
  "The elements of the offense they were building toward",
];

export function AuthorSection() {
  return (
    <Section
      id="author"
      number="02"
      title="The officer still writes the report"
      subtitle={
        <>
          SDPD's own requirement is that the program "assist the officer or investigator
          in writing their reports while allowing the officer/investigator to maintain
          complete control over the report's content and structure." We agree with that
          so strongly that it is the organizing principle of the product.
        </>
      }
    >
      <SubSection
        title="There are only three ways to build one of these"
        subtitle={
          <>
            Every product in this category is some version of one of the three approaches
            below. The difference between them is not output quality. It is where the
            thinking happens.
          </>
        }
      >
        <ApproachTaxonomy />
      </SubSection>

      <SubSection
        title="Approach 01 has been tested, and it did not save time"
        subtitle={
          <>
            This is not a theoretical objection. The leading approach-01 product has been
            through a pre-registered randomized controlled trial, published on{" "}
            {DRAFT_ONE_RCT.venue}, and it produced no measurable time savings at all.
          </>
        }
      >
        <DraftOneStudy />
      </SubSection>

      <Pullquote>
        A body camera will never give you the full story. It cannot give you the officer's
        perspective, the trained judgments and observations behind it, or the reason a
        split-second decision was made.
      </Pullquote>

      <SubSection
        title="Why drafting from body-worn camera alone is the wrong architecture"
        subtitle={
          <>
            Some products generate a narrative directly from body-worn camera audio and
            hand it to the officer to sign. It produces a report quickly. It also
            produces a report that is missing exactly the material a use-of-force review,
            a suppression hearing, or a jury will care about.
          </>
        }
      >
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-lg border border-slate-200 p-5">
            <Eyebrow>What a narrative drafted from the recording gets</Eyebrow>
            <ul className="space-y-2">
              {CAMERA_CAPTURES.map((x) => (
                <li key={x} className="flex gap-2.5 text-sm text-slate-600 leading-relaxed">
                  <span
                    className="shrink-0 w-1.5 h-1.5 rounded-full mt-[7px]"
                    style={{ backgroundColor: C.manualDeep }}
                  />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-5">
            <Eyebrow>What only the officer can tell you</Eyebrow>
            <ul className="space-y-2">
              {ONLY_THE_OFFICER.map((x) => (
                <li key={x} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                  <span
                    className="shrink-0 w-1.5 h-1.5 rounded-full mt-[7px]"
                    style={{ backgroundColor: C.primary }}
                  />
                  <span>{x}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-5">
          <Callout tone="warning" title="The legal problem, stated precisely">
            <p>
              Under <em>Graham v. Connor</em>, the reasonableness of an officer's use of
              force is judged "from the perspective of a reasonable officer on the scene,"
              on the facts and circumstances confronting that officer, including what
              they knew at the moment they acted. That perspective is precisely the
              content a chest-mounted camera does not record.
            </p>
            <p>
              A narrative generated from body-worn footage alone is a transcript with
              better grammar. It documents the encounter without documenting the
              decision. And it invites the one question no officer wants to answer badly:{" "}
              <em>"Officer, did you write this?"</em>
            </p>
          </Callout>
        </div>
      </SubSection>
    </Section>
  );
}

// ===================================================================
// The Draft One randomized controlled trial
//
// Everything attributed to the study here is in the paper. Our own argument
// about authorship is separated into its own callout and labeled as ours.
// ===================================================================

function DraftOneStudy() {
  const s = DRAFT_ONE_RCT;
  return (
    <div>
      <div className="rounded-lg border-2 border-slate-900 overflow-hidden">
        <div className="px-5 py-4 bg-slate-900">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            Peer-reviewed preprint · {s.venue}
          </div>
          <div className="text-white font-semibold leading-snug">{s.title}</div>
          <div className="text-[11px] text-slate-400 mt-1.5">
            {s.design} of {s.productTested} at the {s.agency}. {s.irb}.
          </div>
        </div>

        <div className="grid sm:grid-cols-4 divide-y sm:divide-y-0 sm:divide-x divide-slate-200 border-b border-slate-200">
          <Stat value={String(s.officers)} label="Officers" sub={`${s.control} control, ${s.treatment} treated`} />
          <Stat value={fmtInt(s.reports)} label="Reports in the trial" />
          <Stat value={fmtInt(s.didReports)} label="Reports in the year-long check" />
          <Stat
            value="0"
            label="Minutes saved"
            sub="Not statistically significant"
            danger
          />
        </div>

        <div className="p-5">
          <p className="text-sm text-slate-700 leading-relaxed">
            Officers were randomly assigned to write reports the way they always had or to
            use the AI tool. Across the pre-registered model and four alternative
            specifications, and again in a difference-in-differences analysis spanning a
            full year of {fmtInt(s.didReports)} reports, the effect on report duration was
            not statistically significant. The main model put the treatment coefficient at{" "}
            <span className="font-mono text-xs">{s.coefficient}</span> minutes with a
            standard error of{" "}
            <span className="font-mono text-xs">{s.standardError}</span>, an interval far
            too wide to claim any effect.
          </p>
          <p className="text-sm text-slate-700 leading-relaxed mt-3">
            The authors set that against the vendor's own marketing, which they quote as
            claiming officers spent{" "}
            <strong className="text-slate-900">{s.vendorClaimPct}% less time</strong> on
            report writing. Their conclusion is that{" "}
            <strong className="text-slate-900">
              "no time-savings in report-writing can be expected when using AI-assisted
              report-writing."
            </strong>
          </p>
          <p className="text-base font-semibold text-slate-900 leading-snug mt-4">
            So why did {s.productTested}, a product built to save report-writing time, end
            up not saving any time?
          </p>
        </div>

        <div className="border-t-2" style={{ borderColor: C.danger }}>
          <div className="px-5 py-2.5" style={{ backgroundColor: C.danger }}>
            <div className="text-[10px] uppercase tracking-wider font-bold text-white">
              Our explanation of the null result
            </div>
          </div>
          <div className="px-5 py-4 bg-red-50 space-y-3">
            <p className="text-sm text-slate-700 leading-relaxed">
              A draft composed by a model is not a draft in the officer's voice, and it does
              not contain the officer's reasoning, because the model never had access to it.
              Turning that output into something an officer is willing to swear to means
              reading it closely, finding what is missing, and writing the missing part in.
              That is not editing. It is composing, with an extra step in front of it.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              That editing pass is not short. Officers routinely spend as long reworking a
              machine-written draft into their own voice as they would have spent writing the
              report themselves, and often longer, because they are working around someone
              else's structure instead of building their own. The time moves from writing to
              editing and stays on the shift.
            </p>
            <p className="text-sm text-slate-700 leading-relaxed">
              Which points at the same conclusion the taxonomy above reaches. The time is in
              the thinking, not the typing. A tool that removes the typing and leaves the
              officer to supply the thinking afterward has only moved the work. A tool that
              captures the thinking as the officer speaks it is solving a different problem.
            </p>
            <p className="text-[11px] text-slate-500 leading-relaxed">
              The authors offer their own explanations for the finding. This one is ours, and
              we flag it as our argument rather than theirs.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-5">
        <Callout tone="good" title="The department that ran that trial now uses Police Narratives AI">
          <p>
            Manchester Police Department tested Axon Draft One under experimental
            conditions, found no time savings, and published the result. Today they write
            their reports with Police Narratives AI.
          </p>
          <p>
            The very department that concluded Axon Draft One saved no time is now a Police
            Narratives AI customer and one of the heaviest users on the platform. Of the
            three departments in Section 08 their officers write the longest reports and
            recover the most time per report.
          </p>
          <p>
            We think that is the most useful fact in this section, because we had no hand in
            producing it. It is what a department did after running the most rigorous test of
            approach 01 that exists and reading its own results.
          </p>
        </Callout>
      </div>


      <Source>
        {s.title}. {s.venue}.{" "}
        <a
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 hover:text-blue-900 underline underline-offset-2 break-all"
        >
          {s.url}
        </a>
      </Source>
    </div>
  );
}

function Stat({
  value,
  label,
  sub,
  danger,
}: {
  value: string;
  label: string;
  sub?: string;
  danger?: boolean;
}) {
  return (
    <div className="p-4">
      <div
        className={`text-2xl font-bold tabular-nums ${
          danger ? "text-red-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
      <div
        className={`text-[10px] uppercase tracking-wider font-bold mt-0.5 ${
          danger ? "text-red-700" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{sub}</div>}
    </div>
  );
}

// ===================================================================
// The three-approach taxonomy
//
// The organizing exhibit of section 02. The argument is not that our output
// is better; it is that where the cognitive work sits determines whether the
// officer is genuinely the author, and only one of the three keeps it there
// while also capturing what the recording heard.
// ===================================================================

type Approach = {
  n: string;
  name: string;
  gloss: string;
  input: string;
  captures: string;
  misses: string;
  load: string;
  loadTone: "bad" | "good";
  verdict: string;
  recommended?: boolean;
};

const APPROACHES: Approach[] = [
  {
    n: "01",
    name: "Body-worn camera audio alone",
    gloss: "What most of this category does",
    input:
      "The recording. Most of these products work from the audio track only, transcribing speech and never examining the picture; some have begun incorporating the video as well.",
    captures:
      "What the recording holds. What it cannot hold is anything that was never said aloud or never happened in front of the lens.",
    misses:
      "Everything the camera saw but nobody said aloud, plus everything the officer knew on arrival, perceived outside the frame, and decided in the moment, along with the reasoning behind it.",
    load:
      "Removed from the officer entirely, which sounds like the benefit and is actually the defect. Nobody reconstructed the incident. The officer reads a document and signs it.",
    loadTone: "bad",
    verdict:
      "A transcript with better grammar, attested to by someone who did not write it. Incomplete on exactly the facts a use-of-force review or a suppression hearing turns on.",
  },
  {
    n: "02",
    name: "Officer narration alone",
    gloss: "Dictation, and a real improvement",
    input: "The officer speaking their account of the incident.",
    captures:
      "The officer's full perspective: what they knew, saw, perceived, decided, and why.",
    misses:
      "Verbatim statements, exact sequence, and precise timing, all of which the recording already holds.",
    load:
      "Stays with the officer. They still recall the incident, decide what matters, and compose the account. They are still writing the report; they are simply not typing it.",
    loadTone: "good",
    verdict:
      "The officer's own report, produced far faster. Complete on perspective, weaker on the verifiable detail sitting in a recording nobody opened.",
  },
  {
    n: "03",
    name: "Both, combined",
    gloss: "What Police Narratives AI does",
    input:
      "The recording and the officer's narration, processed as two distinct sources and merged into one report.",
    captures:
      "Verbatim statements, sequence, and attribution from the recording. Observation, perception, reasoning, and decision from the officer.",
    misses: "Nothing either source held.",
    load:
      "Stays with the officer, exactly as in approach 02. The recording does not think for them; it supplies the detail they should not have to reconstruct from memory.",
    loadTone: "good",
    verdict:
      "The complete report, written by the officer, grounded in what the recording actually captured. About three minutes of speaking.",
    recommended: true,
  },
];

function ApproachTaxonomy() {
  return (
    <div className="space-y-3">
      {APPROACHES.map((a) => (
        <div
          key={a.n}
          className={`rounded-lg border overflow-hidden break-inside-avoid ${
            a.recommended ? "border-2 border-emerald-300" : "border-slate-200"
          }`}
        >
          <div
            className={`px-5 py-3 flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b ${
              a.recommended
                ? "bg-emerald-50 border-emerald-200"
                : "bg-slate-50 border-slate-200"
            }`}
          >
            <span
              className="font-mono text-xs font-bold tabular-nums"
              style={{ color: a.recommended ? "#047857" : "#94a3b8" }}
            >
              {a.n}
            </span>
            <span className="text-[15px] font-semibold text-slate-900">{a.name}</span>
            <span
              className={`text-[11px] uppercase tracking-wider font-bold ${
                a.recommended ? "text-emerald-700" : "text-slate-500"
              }`}
            >
              {a.gloss}
            </span>
          </div>

          <div className="px-5 py-4 grid md:grid-cols-2 gap-x-6 gap-y-3">
            <Row label="Input" value={a.input} />
            <Row label="Captures" value={a.captures} />
            <Row label="Misses" value={a.misses} tone={a.misses === "Nothing either source held." ? "good" : "bad"} />
            <Row label="Where the cognitive work sits" value={a.load} tone={a.loadTone} />
          </div>

          <div
            className={`px-5 py-3 border-t text-sm leading-relaxed ${
              a.recommended
                ? "bg-emerald-50/60 border-emerald-200 text-slate-800"
                : "bg-slate-50 border-slate-200 text-slate-700"
            }`}
          >
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-2">
              Result
            </span>
            {a.verdict}
          </div>
        </div>
      ))}

      <Callout tone="warning" title="Approach 01 works from the audio, not the footage">
        <p>
          These products are usually described as writing reports "from body-worn camera
          footage," which overstates what most of them do. They work from the{" "}
          <strong>audio track</strong>: the recording is transcribed for speech, the
          narrative is generated from that transcript, and the picture is never examined.
          A department buys a camera program, records the incident in sound and vision, and
          then drafts the official account of it from the sound alone.
        </p>
        <p>
          Some products have begun taking in the video as well. That improves what the model
          can see. Whether the model reads a transcript or watches footage, though,{" "}
          <strong>the model is still the one deciding</strong> what happened, what was
          material, and how to characterize it. The officer's role is to review and approve
          an account they did not compose.
        </p>
        <p>
          Approach 01's defect is in who does the thinking. A better camera feed produces a
          better-informed guess about an officer's reasoning. It does not produce the
          reasoning.
        </p>
      </Callout>

      <Callout tone="legal" title="The row that separates approach 01 from the other two">
        <p>
          The instinct is to judge these on completeness, and on that axis the third
          approach obviously wins. But the more important distinction is between the first
          approach and the other two.
        </p>
        <p>
          In approaches 02 and 03 the officer is still doing the cognitive work of the
          report: recalling the incident, judging what is material, and deciding how to
          characterize what happened. In approach 01 that work is done by a model reading a
          transcript, and the officer's contribution is a signature. Only one of those
          produces a witness who can be cross-examined about their own report.
        </p>
      </Callout>
    </div>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "good" | "bad";
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-0.5">
        {label}
      </div>
      <div
        className={`text-sm leading-relaxed ${
          tone === "bad"
            ? "text-red-800"
            : tone === "good"
              ? "text-emerald-800"
              : "text-slate-700"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

// ===================================================================
// 3. The dual-input model
// ===================================================================

export function DualInputSection() {
  return (
    <Section
      id="dual-input"
      number="03"
      title="Two recordings, one report"
      subtitle={
        <>
          The officer's account and the recorded encounter are different kinds of
          evidence, and a complete report needs both. Police Narratives AI takes them as
          two distinct inputs and produces a single narrative, written in the officer's
          voice, from the officer's perspective, grounded in what the recording actually
          captured. We call the recorded side <em>interaction audio</em> throughout this
          document, meaning body-worn camera, in-car video, and recorded interviews.
        </>
      }
    >
      <DualInputDiagram />

      <SubSection
        title="How the two inputs differ"
        subtitle="They are processed differently because they are different problems."
      >
        <Table head={["", "Interaction audio (body-worn camera and interviews)", "Narrative dictation"]}>
          <tr>
            <Td strong>What it is</Td>
            <Td>
              Body-worn camera, in-car video, recorded interviews: the encounter as it
              happened
            </Td>
            <Td>The officer speaking their account of the incident</Td>
          </tr>
          <tr>
            <Td strong>Perspective</Td>
            <Td>Third person, multi-party</Td>
            <Td>First person, the reporting officer</Td>
          </tr>
          <tr>
            <Td strong>Processing</Td>
            <Td>Transcribed with speaker diarization, so every speaker is separated and labeled</Td>
            <Td>Transcribed as continuous narration</Td>
          </tr>
          <tr>
            <Td strong>What it contributes</Td>
            <Td>Verbatim statements, sequence, timing, who said what</Td>
            <Td>Observation, perception, reasoning, decision, and the elements</Td>
          </tr>
          <tr>
            <Td strong>Time it takes the officer</Td>
            <Td>None, because the recording already exists</Td>
            <Td>About three minutes of speaking</Td>
          </tr>
        </Table>

        <div className="mt-5">
          <Callout tone="good" title="SDPD's Axon cameras feed this directly">
            <p>
              SDPD already operates more than {SDPD_AXON.bodyCamerasInService.toLocaleString()}{" "}
              Axon body-worn cameras with evidence managed on {SDPD_AXON.evidencePlatform}.
              We integrate with Axon Evidence directly, in production today. The department
              creates an API key once. After that, officers browse their own evidence locker
              from inside the platform and select the recordings for the case.
            </p>
            <p>
              <strong>Nothing is downloaded and nothing is re-uploaded.</strong> The transfer
              runs server-side between Axon and us, so an officer never waits on a
              multi-gigabyte export, never moves an evidence file onto a laptop, and never
              creates a second copy of evidence outside the evidence management system.
              They select the recording and go back in service while it processes.
            </p>
            <p>
              The footage SDPD is already capturing becomes the third-person half of the
              report. The officer supplies the half no camera can.
            </p>
          </Callout>
        </div>
      </SubSection>

      <SubSection
        title="Chain of custody, from observation to filed report"
        subtitle="Every stage is retained and linked to the case. This is what makes an AI-assisted report defensible rather than merely fast."
      >
        <ChainOfCustody />
      </SubSection>
    </Section>
  );
}

// ===================================================================
// Diagrams
// ===================================================================

function DualInputDiagram() {
  return (
    <div className="rounded-lg border border-slate-200 p-5 md:p-7 bg-white">
      <div className="grid md:grid-cols-[1fr_auto_1fr] gap-5 md:gap-6 items-stretch">
        {/* Inputs */}
        <div className="space-y-4">
          <InputCard
            color={C.interaction}
            kicker="Third person"
            title="Interaction audio: body-worn camera and interviews"
            body="Body-worn camera, in-car video, recorded interviews. Transcribed with speaker diarization; the officer names the speakers or the system identifies them."
            foot={`Pulled directly from ${SDPD_AXON.evidencePlatform}`}
          />
          <InputCard
            color={C.primary}
            kicker="First person"
            title="Narrative dictation"
            body="The officer speaks their account: what they knew, saw, perceived, and decided, and why. Roughly three minutes of speaking."
            foot="Recorded in the app, on the phone or in the car"
          />
        </div>

        {/* Join */}
        <div className="flex md:flex-col items-center justify-center gap-2 py-2">
          <div className="hidden md:block w-px flex-1 bg-slate-200" />
          <div className="shrink-0 w-11 h-11 rounded-full bg-slate-900 text-white flex items-center justify-center text-lg font-bold">
            +
          </div>
          <div className="hidden md:block w-px flex-1 bg-slate-200" />
        </div>

        {/* Output */}
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/50 p-5 flex flex-col justify-center">
          <div
            className="text-[11px] uppercase tracking-wider font-bold mb-1.5"
            style={{ color: "#047857" }}
          >
            One report
          </div>
          <h4 className="text-base font-semibold text-slate-900 leading-snug mb-2">
            Written in the officer's voice
          </h4>
          <p className="text-sm text-slate-700 leading-relaxed">
            Verbatim statements and sequence from the recording. Observation, perception,
            reasoning, and decision from the officer. Structured to the agency's report
            format.
          </p>
          <div className="mt-4 pt-3 border-t border-emerald-200 text-xs text-slate-600 leading-relaxed">
            The officer reviews, edits anything, and attests before it leaves the system.
            Nothing is filed that the officer has not read and approved.
          </div>
        </div>
      </div>
    </div>
  );
}

function InputCard({
  color,
  kicker,
  title,
  body,
  foot,
}: {
  color: string;
  kicker: string;
  title: string;
  body: string;
  foot: string;
}) {
  return (
    <div className="rounded-lg border border-slate-200 p-4 border-l-4" style={{ borderLeftColor: color }}>
      <div
        className="text-[10px] uppercase tracking-wider font-bold mb-1"
        style={{ color }}
      >
        {kicker}
      </div>
      <h4 className="text-sm font-semibold text-slate-900 mb-1.5">{title}</h4>
      <p className="text-xs text-slate-600 leading-relaxed">{body}</p>
      <div className="text-[11px] text-slate-400 mt-2 italic">{foot}</div>
    </div>
  );
}

const CHAIN = [
  { title: "Observation", body: "The officer responds and observes." },
  { title: "Recording", body: "Body-worn camera captures the encounter. The officer dictates their account." },
  { title: "Transcript", body: "Both are transcribed. The officer can read, correct, and re-label any of it." },
  { title: "First draft", body: "The AI produces a draft from both sources. This draft is retained permanently." },
  { title: "Officer review", body: "The officer edits freely, because the report is theirs. Suggestions flag anything missing." },
  { title: "Attested export", body: "The officer affirms they reviewed it and the facts are true. The export is recorded." },
];

function ChainOfCustody() {
  return (
    <div className="relative">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
        {CHAIN.map((s, i) => (
          <Step
            key={s.title}
            n={i + 1}
            title={s.title}
            color={i >= 3 ? C.good : C.primary}
          >
            {s.body}
          </Step>
        ))}
      </div>
      <div className="mt-6">
        <Callout title="What is retained">
          <p>
            The source audio, the transcript, the first AI-generated draft, and the final
            exported report are all retained and linked to the case for as long as the
            agency keeps the report. When a defense request or an internal review asks how
            a specific report came to exist, the answer is a record, not a recollection.
          </p>
        </Callout>
      </div>
      <Source>
        Retention behavior is agency-configurable. Recommended configuration for SDPD is
        described in the security and compliance section.
      </Source>
    </div>
  );
}

// ===================================================================
// Hero
// ===================================================================

export function Hero() {
  return (
    <header className="pt-10 md:pt-16">
      <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700 mb-3">
        Response to RFI {RFI.number}
      </div>
      <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-slate-900 leading-[1.1] max-w-4xl">
        Officers should write their own reports.
        <span className="text-slate-400"> They just shouldn't have to type them.</span>
      </h1>
      <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
        A briefing for the San Diego Police Department on AI-assisted police report
        writing: what the technology should do, what California law now requires of it,
        and what it is worth to a department answering{" "}
        {RFI.callsForServicePerYear.toLocaleString()}+ calls for service a year.
      </p>
      <p className="mt-3 text-sm text-slate-500">
        Prepared by <strong className="text-slate-700">Michael Hari</strong>, founder of
        Police Narratives AI, and team.
      </p>

      <FounderLetter />

      <div className="mt-8 grid sm:grid-cols-3 gap-3">
        <HeroStat
          value="~45 min"
          unit="→ 3 min"
          label="Report time"
          detail="A report that takes 45 minutes to write is dictated in about three minutes of speaking."
        />
        <HeroStat
          value="2–3 hr"
          label="Returned per shift"
          detail="For an officer writing three reports in a shift, at the pace observed across three agency deployments."
          accent
        />
        <HeroStat
          value="0–100"
          label="Report score, before the sergeant"
          detail="Every report is scored and critiqued for missing elements while the officer can still fix it."
        />
      </div>
    </header>
  );
}

/**
 * Opening letter. Deliberately first-person and signed: the rest of the
 * document is institutional, and this is the one place a reader hears from a
 * person before the argument starts.
 */
function FounderLetter() {
  return (
    <div className="mt-8 rounded-lg border-l-4 border-blue-600 bg-slate-50 p-6 md:p-7 max-w-3xl">
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-4">
        A note from the founder
      </div>

      <div className="space-y-3 text-[15px] text-slate-700 leading-relaxed">
        <p>
          In RFI {RFI.number}, the City asks a narrow and well-framed question. SDPD
          answers more than {RFI.callsForServicePerYear.toLocaleString()} calls for service
          a year, a large share of them generating a written report, and the department
          wants to know whether AI can shorten the time that takes without taking the
          report away from the officer. The RFI says it plainly: the program must assist
          the officer while allowing them to "maintain complete control over the report's
          content and structure." It asks about software characteristics, integration with
          report submission and retention systems, training, maintenance, regulatory
          services, and pricing structure. It also asks what the City should require when
          it writes the real solicitation.
        </p>
        <p>
          We have answered all of it, and I want to be direct about the one point that
          organizes everything else. The category has largely decided that the goal is to
          remove report writing from the officer. We think that is the wrong goal. The
          officer's account of what they knew, saw, and decided is the report. Our job is
          to stop them from typing it, not to stop them from writing it.
        </p>
        <p>
          This briefing was prepared specifically for San Diego. The figures come from your
          adopted budget, the language data from your city, the statutory analysis from
          California law, and the integration work from the Axon system your officers
          already use. Nothing here is a template with your name inserted at the top.
        </p>
        <p>
          I would welcome the chance to walk your command staff through a real report end
          to end, and I would welcome even more the chance to run a measured pilot in the
          field, with real officers on their normal assignments, and let the results speak.
        </p>
      </div>

      <div className="mt-5 pt-4 border-t border-slate-200">
        <div className="text-sm font-semibold text-slate-900">Michael Hari</div>
        <div className="text-xs text-slate-500">Founder, Police Narratives AI</div>
        <a
          href="mailto:michael@policenarratives.ai"
          className="text-xs text-blue-700 hover:text-blue-900 underline underline-offset-2"
        >
          michael@policenarratives.ai
        </a>
      </div>
    </div>
  );
}

function HeroStat({
  value,
  unit,
  label,
  detail,
  accent,
}: {
  value: string;
  unit?: string;
  label: string;
  detail: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 border ${
        accent
          ? "bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200"
          : "bg-slate-50 border-slate-200"
      }`}
    >
      <div
        className={`text-2xl md:text-[28px] font-bold tabular-nums leading-tight ${
          accent ? "text-emerald-700" : "text-slate-900"
        }`}
      >
        {value}
        {unit && <span className="text-base font-medium ml-1 opacity-60">{unit}</span>}
      </div>
      <div
        className={`text-[11px] uppercase tracking-wider font-bold mt-1 ${
          accent ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      <p className="text-xs text-slate-600 leading-relaxed mt-2">{detail}</p>
    </div>
  );
}
